import {
  Component,
  type ComponentType,
  type ErrorInfo,
  type PropsWithChildren,
  type ReactNode,
} from 'react';

import ErrorFallback from './ErrorFallback';
import { ErrorBoundaryFallbackProps } from './types';

type ErrorBoundaryState = {
  error: Error | null;
};

type Props = {
  fallback?: ComponentType<ErrorBoundaryFallbackProps>;
};

class ErrorBoundary extends Component<
  PropsWithChildren<Props>,
  ErrorBoundaryState
> {
  constructor(props: PropsWithChildren<Props>) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Error caught by Error Boundary:', error, errorInfo);
  }

  render(): ReactNode {
    const { error } = this.state;
    const { fallback: Fallback = ErrorFallback } = this.props;

    if (error) {
      return <Fallback error={error} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
